import * as amplitude from "@amplitude/analytics-browser"
import { Identify } from "@amplitude/analytics-browser"
import type { BrowserClient } from "@amplitude/analytics-browser/lib/esm/types"

import { isBot } from "@bounce/util"

import { AMPLITUDE_PROXY_URL } from "./constants"
import { LoggerIntegration } from "./integrations/logger/logger"
import { UserAgentIntegration } from "./integrations/userAgent/userAgent"
import type { AmplitudeWebPlugin } from "./types"
import { getAnonymousIdAsync } from "../../contexts/AnonymousIdContext"
import { AppNavigationEvents } from "../../events"
import { error } from "../../helpers/error"
import { clearEventsQueue, hasEventInQueue, pushEventToQueue, shouldAddEventToTheQueue } from "../../helpers/events"
import type { AnalyticsClient, UserInfo } from "../../types"
import { AnalyticsEventTypes } from "../../types"

export const Amplitude: AnalyticsClient<BrowserClient> = {
  client: undefined,
  queuedEvents: [],
  isSettingUp: false,
  isTrackingEnabled: true,

  isSetup() {
    return !!this.client
  },

  async setup({ amplitude: config }) {
    if (!config) {
      this.isTrackingEnabled = false
      return
    }

    const { apiKey, debug, logger, webIntegrations = [] } = config

    if (!apiKey || (await isBot()) || this.isSetup() || this.isSettingUp) return
    this.isSettingUp = true

    const plugins: AmplitudeWebPlugin[] = [UserAgentIntegration, ...webIntegrations]

    const anonymousId = await getAnonymousIdAsync()
    await amplitude.init(apiKey, {
      logLevel: debug ? amplitude.Types.LogLevel.Debug : undefined,
      serverUrl: AMPLITUDE_PROXY_URL,
      deviceId: anonymousId,
      autocapture: true,
    }).promise

    // Load custom integrations: Logger, Intercom, UserAgent...
    if (logger && process.env.NODE_ENV !== "test") {
      plugins.push(LoggerIntegration)
    }
    for (const plugin of plugins) {
      await amplitude.add(plugin).promise
    }

    this.client = amplitude

    await clearEventsQueue(this)
    this.isSettingUp = false
  },

  // eslint-disable-next-line max-params
  page(category, name, properties, options) {
    // Replace existing page event in queue with the incoming one
    if (hasEventInQueue(this, AnalyticsEventTypes.PAGE)) {
      this.queuedEvents = this.queuedEvents.filter(e => e.type !== AnalyticsEventTypes.PAGE)
      pushEventToQueue(this, { name, category, properties, type: AnalyticsEventTypes.PAGE })
      return
    }

    if (!this.client || shouldAddEventToTheQueue(this, options)) {
      pushEventToQueue(this, { name, category, properties, type: AnalyticsEventTypes.PAGE })
      return
    }

    this.client.track(AppNavigationEvents.LoadedPage, {
      category,
      name,
      properties,
    })
  },

  screen(_name) {
    throw error("screen method is only supported on native")
  },

  track(eventName, properties, options) {
    if (!this.client || shouldAddEventToTheQueue(this, options)) {
      pushEventToQueue(this, { name: eventName, properties, type: AnalyticsEventTypes.TRACK })
      return
    }

    this.client.track(eventName, properties)
  },

  async identify(userId, traits, options) {
    if (!this.client || shouldAddEventToTheQueue(this, options)) {
      pushEventToQueue(this, { properties: { userId, traits }, type: AnalyticsEventTypes.IDENTIFY }, true)
      return
    }

    this.client.setUserId(userId)
    const identify = new Identify()
    if (traits) {
      Object.entries(traits).forEach(([key, value]) => {
        identify.set(key, value)
      })
    }
    await this.client.identify(identify).promise
  },

  async reset() {
    if (!this.client) return

    return this.client.reset()
  },

  async setAnonymousId(id: string) {
    this.client?.setDeviceId(id)
  },

  async getUser() {
    if (!this.client) return undefined

    const userDetails: UserInfo = {
      userId: this.client.getUserId(),
      anonymousId: this.client.getDeviceId(),
      traits: undefined, // TODO: check if we can get traits
    }

    return userDetails
  },
}
